import type { ComponentRendering } from '@sitecore-content-sdk/nextjs';
import type { SitecoreContextValue } from 'lib/component-props';

export const deepSearch = <T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  root: any,
  predicate: (obj: T) => boolean,
  stopAtFirst = false
) => {
  const processedObjects: object[] = [];
  const matchingObjects: T[] = [];
  (function find(obj) {
    if (obj === null || obj === undefined) {
      return;
    }
    if (predicate(obj) === true) {
      matchingObjects.push(obj as T);
      if (stopAtFirst) {
        return;
      }
    }
    for (const key of Object.keys(obj)) {
      const o = obj[key];
      if (o && typeof o === 'object') {
        if (!processedObjects.find((obj) => obj === o)) {
          processedObjects.push(o);
          find(o);
        }
      }
    }
  })(root);
  return matchingObjects;
};

export function findComponent(
  rendering:
    | ComponentRendering
    | SitecoreContextValue
    | Record<string, unknown>
    | undefined,
  componentName: string
): ComponentRendering[] {
  if (rendering == null) {
    return [];
  }
  const componentData = deepSearch(
    rendering,
    (x: { componentName?: string; uid?: unknown }) =>
      x.componentName === componentName && typeof x.uid === 'string'
  );
  return componentData as unknown as ComponentRendering[];
}

/**
 * Returns the fields of the nearest ancestor Section component for a given child rendering UID.
 * Traverses the rendering tree starting at the provided root and keeps track of the most recent
 * Section encountered on the path to the target child UID.
 */
export function getParentSectionFields<TFields = unknown>(
  root: ComponentRendering | SitecoreContextValue,
  childUid: string
): TFields | undefined {
  const visited = new Set<object>();
  let result: TFields | undefined;

  const visit = (node: unknown, currentSectionFields?: TFields) => {
    if (result) return;
    if (node === null || typeof node !== 'object') return;
    if (visited.has(node as object)) return;
    visited.add(node as object);

    const n = node as { componentName?: string; uid?: string; fields?: unknown };

    let nextSectionFields = currentSectionFields;
    if (n.componentName === 'Section' && typeof n.uid === 'string') {
      nextSectionFields = n.fields as TFields;
    }

    if (n.uid === childUid) {
      result = nextSectionFields;
      return;
    }

    for (const key of Object.keys(node as object)) {
      const value = (node as Record<string, unknown>)[key];
      if (value && typeof value === 'object') {
        if (Array.isArray(value)) {
          for (const item of value) {
            visit(item, nextSectionFields);
            if (result) return;
          }
        } else {
          visit(value, nextSectionFields);
          if (result) return;
        }
      }
    }
  };

  visit(root);
  return result;
}

/**
 * Returns the params of the nearest ancestor Section component for a given child rendering UID.
 * Traverses the rendering tree starting at the provided root and keeps track of the most recent
 * Section encountered on the path to the target child UID.
 */
export function getParentSectionParams<TParams = Record<string, string>>(
  root: ComponentRendering | SitecoreContextValue,
  childUid: string
): TParams | undefined {
  const visited = new Set<object>();
  let result: TParams | undefined;

  const visit = (node: unknown, currentSectionParams?: TParams) => {
    if (result) return;
    if (node === null || typeof node !== 'object') return;
    if (visited.has(node as object)) return;
    visited.add(node as object);

    const n = node as { componentName?: string; uid?: string; params?: unknown };

    let nextSectionParams = currentSectionParams;
    if (n.componentName === 'Section' && typeof n.uid === 'string') {
      nextSectionParams = n.params as TParams;
    }

    if (n.uid === childUid) {
      result = nextSectionParams;
      return;
    }

    for (const key of Object.keys(node as object)) {
      const value = (node as Record<string, unknown>)[key];
      if (value && typeof value === 'object') {
        if (Array.isArray(value)) {
          for (const item of value) {
            visit(item, nextSectionParams);
            if (result) return;
          }
        } else {
          visit(value, nextSectionParams);
          if (result) return;
        }
      }
    }
  };

  visit(root);
  return result;
}
